import React from "react";
import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";

/**
 * Phase 12.3 — Lifted Affiliate Program plain-language terms.
 * Anchor-linked from <ComplianceDisclaimer> via /affiliate-zone#terms.
 *
 * THIS IS NOT LEGAL ADVICE. The real legal terms must be drafted by counsel
 * before launch. This page is a plain-language placeholder so the disclaimer
 * link points somewhere real.
 */
const TERMS = [
  { t: "Participation is approval-based",
    d: "Joining the Lifted Affiliate Program requires registration and Lifted approval. Lifted may decline or revoke participation at any time." },
  { t: "Commissions apply only to qualifying tracked referrals",
    d: "A referral is qualifying when it is tracked through your affiliate link, QR, or code, completes a Lifted-recognized transaction, and is not reversed, refunded, or flagged." },
  { t: "Results vary",
    d: "Outcomes depend on individual effort, network activity, market conditions, product availability, and ecosystem volume. The Lifted Affiliate Program is not a guarantee of income or business success." },
  { t: "No guaranteed earnings",
    d: "Lifted does not guarantee any specific earnings. Tier-up rewards, leaderboard rewards, and bonus opportunities are illustrative — not promises." },
  { t: "Payouts require admin approval",
    d: "Commissions accrue to your affiliate ledger and become payable only after Lifted reviews the underlying activity and a payout batch is approved. Stripe Connect (or equivalent payout rails) must be set up on your account before funds can be transferred." },
  { t: "Driver, partner, and franchise referrals require separate review",
    d: "Introducing a driver, partner store, or franchise candidate does not guarantee acceptance. Drivers must pass screening and background checks. Partner stores must pass compliance review. Franchisees must pass an extended approval process. All standards remain at Lifted's sole discretion." },
  { t: "Compliance and integrity",
    d: "Lifted may review activity that appears suspicious, fraudulent, non-compliant with applicable laws, or in violation of these terms — including but not limited to self-referrals, fake accounts, prohibited promotion of restricted products, misleading earnings claims by affiliates, and unauthorized use of brand assets. Lifted may withhold commissions, suspend access, or terminate participation as a result." },
  { t: "Training and dashboard access unlock after approval",
    d: "Affiliate training, internal dashboards, and team-culture features become available after your affiliate role is approved by Lifted. Public information about the Program remains accessible to anyone." },
  { t: "Program terms may change",
    d: "The Lifted Affiliate Program is part of an evolving ecosystem. Tiers, commission rates, qualifying-activity definitions, and payout cadences may change as the program develops. Lifted will communicate material changes through the affiliate dashboard and registered email." },
  { t: "Brand and content rules",
    d: "Use of LiftedAtTheMoment brand assets must follow the published Brand Asset Library. Misleading content, fake testimonials, and content implying guaranteed income are prohibited." },
];

export default function AffiliateTerms() {
  return (
    <div data-testid="affiliate-terms-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <header className="mb-10">
        <div className="eyebrow inline-flex items-center gap-2"><ScrollText className="h-3 w-3" /> Program terms</div>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase mt-3 leading-[0.95]">
          Lifted Affiliate Program<br /><span className="text-lifted-green">plain-language terms.</span>
        </h1>
        <p className="text-lifted-dim text-sm mt-4">
          These are the rules of the Lifted Affiliate Program in plain language. The formal legal Terms of Use and
          Privacy Policy govern in case of any conflict. By participating you agree to both.
        </p>
      </header>

      <ol className="space-y-4">
        {TERMS.map((row, i) => (
          <li
            key={row.t}
            data-testid={`affiliate-term-${i}`}
            className="glass-panel p-5"
          >
            <div className="font-bebas tracking-[0.18em] text-[0.65rem] text-lifted-mute">RULE {String(i + 1).padStart(2, "0")}</div>
            <div className="font-anton uppercase text-base mt-1 text-lifted-green">{row.t}</div>
            <p className="text-[0.78rem] text-lifted-text mt-2 leading-relaxed">{row.d}</p>
          </li>
        ))}
      </ol>

      <div id="terms" className="mt-12 glass-panel p-6">
        <p className="text-[0.7rem] text-lifted-mute leading-relaxed">
          <strong className="text-lifted-text">Disclaimer.</strong> The Lifted Affiliate Program does not guarantee
          earnings, business success, or specific outcomes. Results vary based on effort, network activity, and
          ecosystem volume. Driver, partner, and franchise applications are subject to independent review and
          approval requirements. Lifted reserves the right to update, suspend, or revoke participation in accordance
          with these terms.
        </p>
        <Link to="/affiliate-zone" className="inline-block mt-4 chip chip--ghost">← Back to the Affiliate Zone</Link>
      </div>
    </div>
  );
}
